import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight, Shield, Sparkles, Leaf } from 'lucide-react';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import { API_URL } from '../lib/utils';

export const Home = () => {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState([]);

  useEffect(() => {
    fetchFeaturedProducts();
  }, []);

  const fetchFeaturedProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      // Sort by displayOrder and take first 4
      const sortedProducts = response.data.sort((a, b) => (a.displayOrder || 999) - (b.displayOrder || 999));
      setFeaturedProducts(sortedProducts.slice(0, 4));
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  return (
    <div className="grain-overlay" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center px-6 lg:px-12 py-24 bg-gradient-to-br from-purple-50 via-purple-25 to-white">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_oyster-healing/artifacts/g0d6agve_IMG_0835.jpeg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: '0.7'
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-purple-900/25 via-purple-800/15 to-purple-700/10 z-0"></div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <p className="text-xs font-mono uppercase tracking-[0.3em] mb-6 text-white/90" data-testid="hero-subtitle">
            20 Years of Scientific Research
          </p>
          <h1 className="text-5xl md:text-7xl font-heading font-light leading-none mb-8 text-white" data-testid="hero-title">
            Organic Innovative Healing
          </h1>
          <p className="text-lg md:text-xl leading-relaxed mb-12 text-white/95" data-testid="hero-description">
            Revolutionary SAC® formulation technology. The world's first safe calcium ion delivery
            system that helps your body replenish and restore its natural reserves.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="rounded-full bg-white text-purple-700 hover:bg-purple-50 px-8 py-6 font-semibold"
              onClick={() => navigate('/shop')}
              data-testid="shop-now-button"
            >
              Shop Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="rounded-full border-2 border-white text-white hover:bg-white hover:text-purple-700 px-8 py-6"
              onClick={() => navigate('/science')}
              data-testid="learn-more-button"
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Products - MOVED UP */}
      <section className="py-24 px-6 lg:px-12 bg-purple-25" data-testid="featured-products">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-mono uppercase tracking-[0.3em] mb-4 text-purple-600">
              Featured Collection
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-light mb-6">
              Our Healing Products
            </h2>
            <p className="text-lg text-purple-700 max-w-2xl mx-auto">
              Discover the power of SAC® formulation across our range of specialized healing products.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>

          <div className="text-center">
            <Button
              size="lg"
              variant="outline"
              className="rounded-full"
              onClick={() => navigate('/shop')}
              data-testid="view-all-button"
            >
              View All Products
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* The Problem Section */}
      <section className="py-24 px-6 lg:px-12 bg-purple-50" data-testid="problem-section">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-heading font-light text-center mb-16">
            The Challenge: Oxidative Stress
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                <Shield className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-heading mb-3">Heavy Metals</h3>
              <p className="text-stone-600 leading-relaxed">
                Conventional supplements often contain harmful toxins and impurities.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                <Sparkles className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-heading mb-3">Poor Absorption</h3>
              <p className="text-stone-600 leading-relaxed">
                Traditional calcium supplements fail to deliver ions effectively to cells.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                <Leaf className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-xl font-heading mb-3">Body Strain</h3>
              <p className="text-stone-600 leading-relaxed">
                Oxidative stress depletes your body's natural healing capabilities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The Solution Section */}
      <section className="py-24 px-6 lg:px-12 bg-purple-100" data-testid="solution-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-mono uppercase tracking-[0.3em] mb-6 text-purple-700">
            Revolutionary Technology
          </p>
          <h2 className="text-4xl md:text-5xl font-heading font-light mb-8 text-purple-900">
            SAC® Formulation: The Solution
          </h2>
          <p className="text-lg leading-relaxed mb-12 text-purple-800">
            Our exclusive hot and cold manufacturing process destroys heavy metals, toxins, and
            impurities. The result? Pure, safe calcium ions that your body can actually use.
          </p>

          <div className="grid md:grid-cols-3 gap-8 text-left">
            <div className="bg-white/60 backdrop-blur-sm p-8 rounded-2xl border border-purple-200">
              <p className="text-xs font-mono uppercase tracking-[0.2em] mb-2 text-purple-700">
                Selenium
              </p>
              <p className="text-sm text-purple-900">Reduces Oxidative Stress</p>
            </div>
            <div className="bg-white/60 backdrop-blur-sm p-8 rounded-2xl border border-purple-200">
              <p className="text-xs font-mono uppercase tracking-[0.2em] mb-2 text-purple-700">
                Calcium
              </p>
              <p className="text-sm text-purple-900">Bone Strength + Cell Health</p>
            </div>
            <div className="bg-white/60 backdrop-blur-sm p-8 rounded-2xl border border-purple-200">
              <p className="text-xs font-mono uppercase tracking-[0.2em] mb-2 text-purple-700">
                Magnesium
              </p>
              <p className="text-sm text-purple-900">Bone + Cardiovascular Health</p>
            </div>
          </div>
        </div>
      </section>

      {/* Customer Reviews Section */}
      <section className="py-24 px-6 lg:px-12 bg-purple-100" data-testid="reviews-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs font-mono uppercase tracking-[0.3em] mb-4 text-purple-600">
              Customer Testimonials
            </p>
            <h2 className="text-4xl md:text-5xl font-heading font-light mb-6 text-purple-900">
              Real Stories, Real Results
            </h2>
            <p className="text-lg text-purple-700 max-w-2xl mx-auto">
              Hear from our customers who have experienced the healing power of SAC® formulation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Review 1 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg" data-testid="review-1">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-500 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-xl">★</span>
                  ))}
                </div>
              </div>
              <p className="text-purple-800 mb-4 leading-relaxed">
                "Fantastic!!"
              </p>
              <div className="border-t border-purple-200 pt-4">
                <p className="font-heading text-lg text-purple-900">Mary</p>
                <p className="text-sm text-purple-600">Canada • August 30, 2025</p>
                <p className="text-xs text-purple-500 mt-2">Bone Health Bundle</p>
              </div>
            </div>

            {/* Review 2 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg" data-testid="review-2">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-500 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-xl">★</span>
                  ))}
                </div>
              </div>
              <p className="text-purple-800 mb-4 leading-relaxed">
                "I was having to use a walker and had no energy but now I can carry my oxygen and walk unassisted! I also do 6 laps on the rooftop walk about each evening and a half hr of lazy fit on my phone app as well. I now take the stairs going down to the main floor several times a day which is 7 steps and 11 flights as I live on the 5th floor so it is really working! I have also just put in a drinking water tap!"
              </p>
              <div className="border-t border-purple-200 pt-4">
                <p className="font-heading text-lg text-purple-900">Mary A.</p>
                <p className="text-sm text-purple-600">Canada • August 13, 2025</p>
              </div>
            </div>

            {/* Review 3 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg" data-testid="review-3">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-500 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-xl">★</span>
                  ))}
                </div>
              </div>
              <p className="text-purple-800 mb-4 leading-relaxed">
                "I have been taking the Marah-Cel for about 6 months now. Originally before I even started it, I had several health problems. My hormones were out of whack, thyroid out of whack, high cholesterol, high blood pressure, high liver enzymes and terrible anxiety and panic attacks. When I started the Marah-Cel though, I started because I had an awful fall on January 17th 2025, where I broke my tibia, fibula and an ankle bone. My tibia came out of my leg. It was brutal. I had to have emergency surgery. Was told it would take 6 months to a year before I would be able to walk again. Now my leg has healed incredibly well and fast, but my testimony for today has to do with all my other blood markers. For the first time in years, all my blood work is trending for the better. I usually hate having my blood drawn but am actually looking forward to seeing the results in another 6 months of being on Marah-Cel!"
              </p>
              <div className="border-t border-purple-200 pt-4">
                <p className="font-heading text-lg text-purple-900">Cassandra</p>
                <p className="text-sm text-purple-600">USA • July 14, 2025</p>
                <p className="text-xs text-purple-500 mt-2">Marah-Cel</p>
              </div>
            </div>

            {/* Review 4 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg" data-testid="review-4">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-500 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-xl">★</span>
                  ))}
                </div>
              </div>
              <p className="text-purple-800 mb-4 leading-relaxed">
                "I love it. I had gums problems and now I don't have any problems. Also I have strong nails."
              </p>
              <div className="border-t border-purple-200 pt-4">
                <p className="font-heading text-lg text-purple-900">Hyon</p>
                <p className="text-sm text-purple-600">USA • February 9, 2025</p>
              </div>
            </div>

            {/* Review 5 */}
            <div className="bg-white p-8 rounded-2xl shadow-lg" data-testid="review-5">
              <div className="flex items-center mb-4">
                <div className="flex text-yellow-500 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <span key={i} className="text-xl">★</span>
                  ))}
                </div>
              </div>
              <p className="text-purple-800 mb-4 leading-relaxed">
                "It helps me to stay healthy and make sure my bones stay strong as I get older."
              </p>
              <div className="border-t border-purple-200 pt-4">
                <p className="font-heading text-lg text-purple-900">Michelle</p>
                <p className="text-sm text-purple-600">USA • January 24, 2025</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 px-6 lg:px-12 bg-purple-25" data-testid="trust-section">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <img
                src="https://images.pexels.com/photos/2280547/pexels-photo-2280547.jpeg?w=800"
                alt="Scientific research laboratory"
                className="rounded-2xl shadow-lg"
              />
            </div>
            <div>
              <p className="text-xs font-mono uppercase tracking-[0.3em] mb-4 text-purple-600">
                20 Years of Research
              </p>
              <h2 className="text-4xl font-heading font-light mb-6 text-purple-900">
                Our Scientific Vision
              </h2>
              <p className="text-lg leading-relaxed text-purple-800 mb-6">
                Two decades of dedicated research revolutionized the way we heal our bodies.
                Our groundbreaking SAC® formulation technology represents the pinnacle
                of organic healing innovation.
              </p>
              <p className="text-lg leading-relaxed text-purple-800 mb-8">
                Every product undergoes our exclusive manufacturing process, ensuring purity and
                efficacy that conventional supplements simply cannot match.
              </p>
              <Button
                variant="default"
                className="rounded-full"
                onClick={() => navigate('/about')}
                data-testid="our-story-button"
              >
                Read Our Story
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};